
import { useState, useEffect } from 'react';
import { Editor } from '@tiptap/react';
import { ImageIcon, AlertCircle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Drawer, DrawerContent } from "@/components/ui/drawer";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { mediaService, ImageLocation, UploadedImage } from '@/services/mediaService';

interface MediaLibraryProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  editor: Editor;
  onImageUpload: (file: File) => void;
  isUploading: boolean;
}

export default function MediaLibrary({ 
  isOpen, 
  onOpenChange, 
  editor, 
  onImageUpload,
  isUploading 
}: MediaLibraryProps) {
  const [uploadedImages, setUploadedImages] = useState<UploadedImage[]>([]);
  const [error, setError] = useState<string | null>(null);
  
  // Fetch previously uploaded images when drawer opens
  useEffect(() => {
    if (isOpen) {
      fetchUploadedImages();
    }
  }, [isOpen]);
  
  const fetchUploadedImages = async () => {
    try {
      setError(null);
      const images = await mediaService.fetchImages(ImageLocation.ARTICLE_CONTENT);
      setUploadedImages(images);
    } catch (err: any) {
      setError(err.message || 'Failed to load media library');
    }
  };
  
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      onImageUpload(file);
      // Reset the file input
      event.target.value = '';
    }
  };
  
  const insertExistingImage = (imageUrl: string) => {
    if (!editor) return;
    
    editor.chain()
      .focus()
      .insertContent({
        type: 'customImage',
        attrs: { 
          src: imageUrl, 
          alt: 'Article image',
          caption: '',
          alignment: 'center',
          width: '100%'
        }
      })
      .run();
    
    onOpenChange(false);
  };
  
  return (
    <Drawer open={isOpen} onOpenChange={onOpenChange}>
      <DrawerContent className="bg-gray-900">
        <div className="p-4">
          <h3 className="text-xl font-bold mb-4">Media Library</h3>
          
          <div className="mb-6">
            <label 
              htmlFor="upload-image-library" 
              className="cursor-pointer flex flex-col items-center justify-center p-6 border-2 border-dashed border-white/20 rounded-md hover:border-white/40 transition-colors"
            >
              <ImageIcon className="h-8 w-8 mb-2 text-white/60" />
              <span className="text-white/80">Click to upload new image</span>
              <span className="text-sm text-white/50 mt-1">Supports: JPG, PNG, GIF, WebP (max 5MB)</span>
              <Input
                id="upload-image-library"
                type="file"
                accept="image/*"
                className="hidden"
                disabled={isUploading}
                onChange={handleFileChange}
              />
            </label>
          </div>
          
          {isUploading && (
            <div className="bg-blue-950/50 p-2 rounded-md mb-4 flex items-center">
              <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
              <span>Uploading image...</span>
            </div>
          )}
          
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          
          {uploadedImages.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {uploadedImages.map((image, index) => (
                <div 
                  key={index} 
                  className="border border-white/20 rounded-md overflow-hidden hover:border-white/60 transition-colors cursor-pointer"
                  onClick={() => insertExistingImage(image.publicUrl)}
                >
                  <AspectRatio ratio={16/9} className="bg-black/50">
                    <img 
                      src={image.publicUrl} 
                      alt={`Uploaded image ${image.fileName}`} 
                      className="w-full h-full object-cover"
                    />
                  </AspectRatio>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 text-white/60">
              <p>No images in library yet</p>
            </div>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
}
